import {
  DataTableSource,
  TableRow,
  type DataTablePageOptions,
  type DataTablePageReq,
} from "@carbon6/c6-frontend-design";
import {
  listAdminUploadJobs,
  type AdminUploadJob,
  type AdminUploadJobQuery,
} from "@/api/adminUploadJob";
import {
  listAdminDiagnosticJobs,
  type AdminDiagnosticJob,
  type AdminDiagnosticJobQuery,
  type AdminDiagnosticCapacity,
} from "@/api/adminDiagnosticJob";
import { adminOperationError } from "@/app/adminOperationError";

export class UploadJobTableRow extends TableRow {
  constructor(
    readonly job: AdminUploadJob,
    private readonly onOpen: (uuid: string) => void,
  ) {
    super(null);
  }
  get status() {
    return this.job.status;
  }
  get progress() {
    return this.job.commit_cursor;
  }
  get modifiedAt() {
    return this.job.modified_at;
  }
  get isRowClickable() {
    return true;
  }
  onRowClick() {
    this.onOpen(this.job.uuid);
  }
  getRowId() {
    return this.job.uuid;
  }
  canShow() {
    return true;
  }
  canSelect() {
    return false;
  }
}
export class UploadJobTableSource extends DataTableSource<UploadJobTableRow> {
  observedAt: string | null = null;
  constructor(
    private readonly query: AdminUploadJobQuery,
    private readonly onOpen: (uuid: string) => void,
  ) {
    super({
      initialPage: query.page,
      initialPageSize: query.size,
      resolveErrorMessage: adminOperationError,
    });
  }
  protected override async onPage(req: DataTablePageReq, { signal }: DataTablePageOptions) {
    const response = await listAdminUploadJobs({ ...this.query, ...req }, { signal });
    this.observedAt = response.observed_at;
    return {
      ...response,
      rows: response.items.map((job) => new UploadJobTableRow(job, this.onOpen)),
    };
  }
}
export class DiagnosticJobTableRow extends TableRow {
  constructor(
    readonly job: AdminDiagnosticJob,
    private readonly onOpen: (uuid: string) => void,
  ) {
    super(null);
  }
  get status() {
    return this.job.status;
  }
  get progress() {
    return this.job.completed_count;
  }
  get modifiedAt() {
    return this.job.modified_at;
  }
  get kind() {
    return this.job.kind;
  }
  get reset() {
    return this.job.reset;
  }
  get isRowClickable() {
    return true;
  }
  onRowClick() {
    this.onOpen(this.job.uuid);
  }
  getRowId() {
    return this.job.uuid;
  }
  canShow() {
    return true;
  }
  canSelect() {
    return false;
  }
}
export class DiagnosticJobTableSource extends DataTableSource<DiagnosticJobTableRow> {
  observedAt: string | null = null;
  capacity: AdminDiagnosticCapacity | null = null;
  constructor(
    private readonly query: AdminDiagnosticJobQuery,
    private readonly onOpen: (uuid: string) => void,
  ) {
    super({
      initialPage: query.page,
      initialPageSize: query.size,
      resolveErrorMessage: adminOperationError,
    });
  }
  protected override async onPage(req: DataTablePageReq, { signal }: DataTablePageOptions) {
    const response = await listAdminDiagnosticJobs({ ...this.query, ...req }, { signal });
    this.observedAt = response.observed_at;
    this.capacity = response.capacity;
    return {
      ...response,
      rows: response.items.map((job) => new DiagnosticJobTableRow(job, this.onOpen)),
    };
  }
}
